import React, { useEffect, useState } from "react";
import LandingPage from "./components/LandingPage";
import Header from "./components/Header";
import UISandbox from "./components/UISandbox";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import AnalyticsDashboard from "./components/analytics/AnalyticsDashboard";
import { clearAuthSession, isAuthenticated } from "./services/authSession";
import Footer from "./components/Footer";

type RouteKey = "home" | "sandbox" | "login" | "register" | "dashboard";

function getRouteFromHash(hash: string): RouteKey {
  if (hash.startsWith("#/dashboard")) return "dashboard";
  // Legacy alias: keep existing analytics link functional.
  if (hash.startsWith("#/analytics")) return "dashboard";
  if (hash.startsWith("#/login")) return "login";
  if (hash.startsWith("#/register")) return "register";
  if (hash.startsWith("#/sandbox")) return "sandbox";
  return "home";
}

function isProtectedRoute(route: RouteKey): boolean {
  return route === "dashboard";
}

export default function App() {
  const [route, setRoute] = useState<RouteKey>(
    getRouteFromHash(window.location.hash || "#/"),
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isAuthenticated());

  useEffect(() => {
    function onHashChange() {
      const nextHash = window.location.hash || "#/";
      const nextRoute = getRouteFromHash(nextHash);
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);

      if (isProtectedRoute(nextRoute) && !authenticated) {
        if (!nextHash.startsWith("#/login")) {
          window.location.hash = "#/login";
          return;
        }
      }

      setRoute(nextRoute);
    }

    onHashChange();
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  function handleLogout() {
    clearAuthSession();
    setIsLoggedIn(false);
    if (window.location.hash !== "#/login") {
      window.location.hash = "#/login";
      return;
    }
    setRoute("login");
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header
        active={route}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
      />
      <main className="flex-grow container mx-auto px-4 py-8">
        {route === "home" && <LandingPage />}
        {route === "sandbox" && <UISandbox />}
        {route === "login" && <LoginPage />}
        {route === "register" && <RegisterPage />}
        {route === "dashboard" && <AnalyticsDashboard />}
      </main>
      <Footer />
    </div>
  );
}
